// apps/landing/src/pages/LandingPage.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { ServiceCard } from '@agb/ui';
import { User, Cpu } from 'lucide-react';

const services = [
  { title: 'Soy Cliente', description: 'Solicita y gestiona servicios de mantenimiento y reparacion de tu equipo.', link: '/wizard/', icon: User },
  { title: 'Soy Tecnico', description: 'Gestiona Ordenes de servicio, diagnosticos y agenda de trabajo.', link: '/tech/', icon: Cpu },
];

type HeroVariant = 'plain' | 'image' | 'imageResponsive';
const HERO_VARIANT = 'image' as HeroVariant;

/* ====== HERO VARIANTES ====== */

const HeroPlain: React.FC = () => (
  <header className="py-16 text-center">
    <motion.h1
      className="text-5xl md:text-6xl font-extrabold tracking-tight text-blue-700 mb-4"
      initial={{ opacity: 0, y: -30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      AGB Servicios Integrales
    </motion.h1>
    <motion.p
      className="text-lg md:text-xl max-w-3xl mx-auto text-slate-600"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3, duration: 0.8 }}
    >
      Tu solución integral para Mantenimiento Informático y Reparación en general
    </motion.p>
  </header>
);

const HeroWithImage: React.FC = () => (
  <header
    className="relative text-center bg-cover bg-center"
    style={{ backgroundImage: "url('/assets/imagen03.webp')" }}
  >
    {/* Overlay más fuerte + fade al fondo */}
    <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(2,6,23,0.75),rgba(2,6,23,0.45)_40%,transparent_70%)]" />
    <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-b from-transparent to-slate-50" />

    <div className="relative z-10 py-16 md:py-24">
      <motion.h1
        className="text-5xl md:text-6xl font-extrabold tracking-tight mb-3
                   bg-clip-text text-transparent
                   bg-gradient-to-r from-blue-400 to-blue-600
                   drop-shadow-[0_2px_2px_rgba(0,0,0,0.55)]"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        AGB Servicios Integrales
      </motion.h1>

      {/* Chip translúcido para el subtítulo */}
      <div className="mx-auto inline-block rounded-full bg-slate-900/45 backdrop-blur-sm px-4 py-2">
        <motion.p
          className="text-base md:text-lg text-blue-50 drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          Tu solución integral para Mantenimiento Informático y Reparación en general
        </motion.p>
      </div>
    </div>
  </header>
);

/** Si usas esta variante, añade en index.css las reglas de .hero-bg (variables responsive) */
const HeroResponsive: React.FC = () => (
  <header className="relative text-center bg-cover bg-center hero-bg">
    <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-transparent" />
    <div className="relative z-10 py-16 md:py-20">
      <motion.h1
        className="text-5xl md:text-6xl font-extrabold tracking-tight text-white drop-shadow mb-4"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        AGB Servicios Integrales
      </motion.h1>
      <motion.p
        className="text-lg md:text-xl max-w-3xl mx-auto text-slate-100/90"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.8 }}
      >
        Tu solución integral para Mantenimiento Informático y Reparación en general
      </motion.p>
    </div>
  </header>
);

/* Render según variante */
const Hero: React.FC = () => {
  switch (HERO_VARIANT) {
    case 'plain': return <HeroPlain />;
    case 'image': return <HeroWithImage />;
    case 'imageResponsive': return <HeroResponsive />;
    default: return null;
  }
};

/* ====== LANDING ====== */

const LandingPage: React.FC = () => (
  <div className="min-h-screen flex flex-col bg-slate-50 text-slate-800">
    <Hero />

    <main className="flex-1 flex items-start py-10">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 justify-items-center">
          {services.map((svc, i) => (
            <ServiceCard
              key={i}
              title={svc.title}
              description={svc.description}
              icon={svc.icon}
              link={svc.link}
              className="w-full max-w-md p-10 min-h-[180px] rounded-3xl
                         ring-1 ring-slate-200 bg-white shadow-xl
                         hover:shadow-2xl hover:-translate-y-0.5 hover:ring-blue-200
                         transition-all"
              whileHover={{ y: -2 }}
              transition={{ type: 'spring', stiffness: 260 }}
            />
          ))}
        </div>
      </div>
    </main>

    <footer className="bg-blue-950 text-blue-100 py-6 text-center border-t border-blue-900">
      <p>© 2025 AGB Servicios. Todos los derechos reservados.</p>
      <p className="mt-2 italic">Agustin Pérez, Director</p>
    </footer>
  </div>
);

export default LandingPage;
